import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { TASK_MEMBERS } from "./tasksConstants";

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
};

function norm(s: string) {
  return s.trim().toLowerCase();
}

export function AssigneesMultiSelect({ value, onChange, placeholder = "Choose…", className }: Props) {
  const selected = useMemo(() => new Set(value), [value]);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const detailsRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      const el = detailsRef.current;
      if (!el?.open) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      el.open = false;
      setQ("");
    }
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const filtered = useMemo(() => {
    const qq = norm(q);
    if (!qq) return TASK_MEMBERS;
    return TASK_MEMBERS.filter((m) => norm(m).includes(qq));
  }, [q]);

  function toggle(name: string) {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(Array.from(next));
  }

  return (
    <details
      ref={detailsRef}
      className={[
        "mt-2 rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm [&>summary::-webkit-details-marker]:hidden [&>summary::marker]:content-none relative",
        className ?? "",
      ].join(" ")}
      onToggle={(e) => {
        const open = (e.currentTarget as HTMLDetailsElement).open;
        if (open) setTimeout(() => inputRef.current?.focus(), 0);
        else setQ("");
      }}
    >
      <summary className="cursor-pointer select-none font-semibold">
        {value.length ? `${value.length} selected` : placeholder}
      </summary>

      <div className="absolute left-0 top-full z-50 mt-2 w-[min(26rem,calc(100vw-2rem))] rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/10">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-blue/45" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type to search…"
            className="w-full rounded-md bg-brand-gray/35 py-2 pl-9 pr-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
          />
        </div>

        <div className="mt-2 max-h-56 overflow-auto">
          {filtered.length === 0 ? (
            <p className="px-2 py-2 text-xs text-brand-blue/60">No matches.</p>
          ) : (
            filtered.map((m) => {
              const checked = selected.has(m);
              return (
                <label key={m} className="flex items-center gap-2 px-2 py-1.5">
                  <input type="checkbox" checked={checked} onChange={() => toggle(m)} />
                  <span className="text-sm">{m}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </details>
  );
}

